/* configurator-picker.js — <pdp-configurator-picker>, the dialog that lists every
   bundle configurator a ski belongs to.

   Rendered by `snippets/product-bundle-cta.liquid`, which is shared by the PDP
   (`sections/main-product.liquid`) and the quick view (`sections/quick-view.liquid`),
   so this element has to exist on collection/search pages too — hence its own file
   rather than a block inside pdp.js.

   A ski can be listed in several configurators' ski steps, so the deep link is not
   a single destination. One match still renders a plain <a>; two or more render
   this element, and the customer picks.

   Native <dialog>.showModal() rather than Dawn's <modal-dialog>: it gives the top
   layer, Esc and a focus trap for free, and — unlike <modal-dialog>, which appends
   itself to <body> on connect — it stays inside its host, so product-info's product
   swap (or the quick view emptying itself on close) disposes of it with the rest of
   the markup instead of leaving an orphan behind. */

if (!customElements.get('pdp-configurator-picker')) {
  customElements.define(
    'pdp-configurator-picker',
    class PdpConfiguratorPicker extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-picker-dialog]');
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

        this.querySelector('[data-picker-open]')?.addEventListener('click', () => this.open());
        this.querySelector('[data-picker-close]')?.addEventListener('click', () => this.dialog.close());

        // Click on the backdrop: the dialog box itself is the event target only
        // outside the panel, since the panel covers the rest of it.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });

        // showModal() blocks interaction but not background scroll — the theme
        // parks that on <body>, same as Dawn's drawers and modals. Inside the quick
        // view the surrounding ModalDialog has already set it and still needs it,
        // so only give back what this dialog itself took.
        this.dialog.addEventListener('close', () => {
          if (!this.lockedScroll) return;
          this.lockedScroll = false;
          document.body.classList.remove('overflow-hidden');
        });

        // Esc closes the native dialog on keydown; the keyup that follows still
        // bubbles, and Dawn's ModalDialog closes on keyup. Without this, one Esc
        // inside the quick view would shut the picker AND the quick view behind it.
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.dialog.open) this.swallowEscape = true;
        });
        this.addEventListener('keyup', (event) => {
          if (event.key !== 'Escape' || !this.swallowEscape) return;
          this.swallowEscape = false;
          event.stopPropagation();
        });
      }

      open() {
        if (!document.body.classList.contains('overflow-hidden')) {
          this.lockedScroll = true;
          document.body.classList.add('overflow-hidden');
        }
        this.dialog.showModal();
      }
    }
  );
}
